import React, { useRef, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { FaPlus, FaUpload, FaVideo, FaImage, FaArrowLeft, FaTrash } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import {
    addReel,
    getProjectList,
    viewProjectVideos,
    removeProjectVideo,
} from "../../Redux/Actions/projectActions";
import toast from "react-hot-toast";
import "./addVideo.css"
import {
    completeMultipartUpload,
    generatePreSignedUrls,
    s3MultipartUploadStart,
    s3Upload,
    splitFileIntoChunks,
} from "../../Utils/s3Upload.jsx";

const VideoModal = ({ onClose }) => {
    const dispatch = useDispatch();
    const { project, loading } = useSelector((state) => state.projects || {});
    const videos = project?.data?.builder_videos || [];

    const [mode, setMode] = useState("view");
    const existingVideos = project?.data?.builder_videos;
    const [builderVideos, setBuilderVideos] = useState({});
    const [uploading, setUploading] = useState(false);

    const imageInputRef = useRef(null);
    const thumbnailInputRef = useRef(null);

    const [uploadProgress, setUploadProgress] = useState(0);

    const [playingIndex, setPlayingIndex] = useState(null);

    const handleImageInput = async (e) => {
        const file = e.target.files[0];
        if (!file) {
            toast.error("No file selected");
            return;
        }
        const s3Link = await s3Upload(file, dispatch);
        if (!s3Link) {
            toast.error("Failed to upload image");
            return;
        }
        setBuilderVideos({
            ...builderVideos,
            thumbnail: s3Link,
        });
    };

    const handleThumbnailRemove = (e) => {
        e.preventDefault();
        setBuilderVideos({
            ...builderVideos,
            thumbnail: "",
        });
    };

    const openimageBrowse = (ref) => {
        if (!loading && !uploading) {
            ref.current.click();
        }
    };

    const generateObjectId = () => {
        const hexChars = "0123456789abcdef";
        let objectId = "";
        for (let i = 0; i < 24; i++) {
            objectId += hexChars[Math.floor(Math.random() * 16)];
        }
        return objectId;
    };

    const handleRemoveImage = (e) => {
        e.preventDefault();
        setBuilderVideos({
            ...builderVideos,
            video: "",
        });
    };

    const handleBack = () => {
        if (mode === "view") {
            onClose();
        } else {
            setMode("view");
            setBuilderVideos({});
        }
    };

    const handleRemoveUpdate = (index) => {
        const updates = [...existingVideos];
        updates.splice(index, 1);
        const updatedProject = {
            id: project?.data?._id,
            builder_videos: updates,
        };
        dispatch(addReel(updatedProject)).then(() => {
            const projectListPayload = {
                status: ["approved", "published", "unpublished", "sold", "requested"],
                page: 1,
                limit: 1000,
                is_deleted: false,
            };
            dispatch(getProjectList(projectListPayload));
            setBuilderVideos({});
        });
    };

    const handleScroll = (e) => {
        e.stopPropagation();
    };

    useEffect(() => {
        if (mode === "view" && project?.data?._id) {
            dispatch(viewProjectVideos(project.data._id));
        }
    }, [mode, dispatch, project?.data?._id]);

    const handleVideoInput = async (e) => {
        const file = e.target.files[0];
        if (!file) {
            toast.error("No file selected");
            return;
        }

        const videoElement = document.createElement("video");
        videoElement.preload = "metadata";

        videoElement.onloadedmetadata = async () => {
            window.URL.revokeObjectURL(videoElement.src);
            const duration = videoElement.duration;

            if (duration > 300) {
                toast.error("Only videos up to 5 minutes are allowed.");
                setUploading(false);
                setUploadProgress(0);
                return;
            }

            try {
                setUploading(true);
                setUploadProgress(0);

                const key = file.name;

                const uploadResult = await s3MultipartUploadStart(key);
                const uploadId = uploadResult?.data;
                const chunks = splitFileIntoChunks(file);
                const chunkCount = chunks.length;

                const presignedUrls = await generatePreSignedUrls(key, uploadId, chunkCount);
                const urls = presignedUrls?.data;

                const parts = [];
                for (let i = 0; i < chunkCount; i++) {
                    const chunk = chunks[i];
                    const url = urls[i];
                    const uploadRes = await fetch(url, { method: "PUT", body: chunk });

                    if (!uploadRes.ok) toast.error(`Failed to upload chunk ${i + 1}`);

                    const etag = uploadRes.headers.get("ETag");
                    const rawEtag = etag.replace(/^"(.+)"$/, "$1");
                    parts.push({ ETag: rawEtag, PartNumber: i + 1 });

                    setUploadProgress(Math.round(((i + 1) / chunkCount) * 100));
                }
                await completeMultipartUpload(key, uploadId, parts);
                const fileUrl = `https://${process.env.REACT_APP_AWS_BUCKET}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${encodeURIComponent(key)}`;

                setBuilderVideos({
                    ...builderVideos,
                    video: fileUrl,
                });
            } catch (error) {
                toast.error("Failed to upload video.");
            } finally {
                setUploading(false);
                setUploadProgress(0);
            }
        };

        videoElement.onerror = () => {
            toast.error("Failed to read video metadata.");
        };

        videoElement.src = URL.createObjectURL(file);
    };

    const handleSave = (e) => {
        e.preventDefault();
        const updates = [...(existingVideos || [])];
        if (
            typeof builderVideos.index === "number" &&
            builderVideos.index >= 0 &&
            builderVideos.index < updates.length
        ) {
            updates[builderVideos.index] = { ...builderVideos };
            delete updates[builderVideos.index].index;
        } else {
            updates.push({ ...builderVideos });
        }
        const updatedProject = {
            id: generateObjectId(),
            key: builderVideos.video ? decodeURIComponent(builderVideos.video.split("/").pop()) : "",
            project_id: project?.data?._id || "",
            thumbnail: builderVideos.thumbnail || ""
        };
        dispatch(addReel(updatedProject)).then(() => {
            setMode("view");
            const projectListPayload = {
                status: ["approved", "published", "unpublished", "sold", "requested"],
                page: 1,
                limit: 1000,
                is_deleted: false,
            };
            dispatch(getProjectList(projectListPayload));
            setBuilderVideos({});
        });
    };

    return ReactDOM.createPortal(
        <div
            className="modal-overlay"
            onClick={onClose}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.75)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '20px'
            }}
        >
            <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
                onWheel={handleScroll}
                style={{
                    background: "linear-gradient(rgb(13, 21, 43) 0%, rgb(44 54 82) 100%), url(/static/media/background.e90c60b….png)",
                    borderRadius: '24px',
                    maxWidth: '1000px',
                    width: '100%',
                    maxHeight: '90vh',
                    overflow: 'hidden',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {/* Header */}
                <div style={{
                    padding: '32px 32px 24px',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'linear-gradient(rgb(13, 21, 43) 0%, rgb(44, 71, 145) 100%)',
                    backdropFilter: 'blur(10px)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                        <div style={{
                            width: '56px',
                            height: '56px',
                            borderRadius: '16px',
                            background: "transparent",
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <FaVideo size={28} color="white" />
                        </div>
                        <div>
                            <h2 style={{ color: 'white', margin: 0, fontSize: '28px', fontWeight: '700' }}>
                                Project Videos
                            </h2>
                            <h3 style={{ color: 'rgba(255, 255, 255, 0.8)', margin: '4px 0 0', fontSize: '14px' }}>
                                {mode === 'view' ? 'Manage your video collection' : 'Upload new content'}
                            </h3>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        <div style={{ marginTop: "-99px", marginLeft: "90%", position: "absolute" }}>
                            <button
                                onClick={onClose}
                                style={{
                                    // padding: "14px 32px",
                                    // borderRadius: "12px",
                                    // border: "2px solid #e2e8f0",
                                    background: "transparent",
                                    // color: "#4a5568",
                                    // fontWeight: 600,
                                    cursor: "pointer",
                                    // fontSize: "14px",
                                    transition: "all 0.3s ease",
                                    // marginRight: "12px"
                                }}
                            >
                                <p style={{ color: "rgb(54, 58, 75)", fontSize: "22px" }}>x</p>
                            </button>
                        </div>
                        <button
                            disabled={loading || uploading}
                            onClick={() => (mode === "view" ? setMode("edit") : handleBack())}
                            style={{
                                padding: '12px 24px',
                                borderRadius: '12px',
                                border: '1px solid',
                                background: mode === 'view' ? 'white' : 'transparent',
                                color: mode === 'view' ? 'rgb(54 58 75)' : 'white',
                                fontWeight: '600',
                                cursor: loading || uploading ? 'not-allowed' : 'pointer',
                                opacity: loading || uploading ? 0.5 : 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                fontSize: '14px',
                                transition: 'all 0.3s ease',
                                boxShadow: mode === 'view' ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none'
                            }}
                        >
                            {mode === 'view' ? <FaPlus size={14} /> : <FaArrowLeft size={14} />}
                            {loading || uploading ? "Loading..." : mode === "view" ? "Add New Video" : "Back"}
                        </button>

                        {typeof builderVideos.index === "number" && builderVideos.index >= 0 && (
                            <button
                                disabled={loading || uploading}
                                onClick={() => handleRemoveUpdate(builderVideos.index)}
                                style={{
                                    padding: '12px 24px',
                                    borderRadius: '12px',
                                    border: '2px solid rgba(255, 255, 255, 0.3)',
                                    background: 'transparent',
                                    color: 'white',
                                    fontWeight: '600',
                                    cursor: loading || uploading ? 'not-allowed' : 'pointer',
                                    opacity: loading || uploading ? 0.5 : 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    fontSize: '14px',
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                <FaTrash size={14} />
                                Remove Video
                            </button>
                        )}
                    </div>
                </div>

                {/* Content Area */}
                <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '32px',
                    background: 'white'
                }}>
                    {mode === "edit" ? (
                        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                            {/* Upload Grid */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
                                {/* Video Upload */}
                                <div style={{
                                    border: '3px dashed #e0e0e0',
                                    borderRadius: '16px',
                                    padding: '32px',
                                    textAlign: 'center',
                                    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                                    position: 'relative',
                                    transition: 'all 0.3s ease'
                                }}>
                                    <input
                                        type="file"
                                        accept="video/*"
                                        ref={imageInputRef}
                                        onChange={handleVideoInput}
                                        hidden
                                        disabled={loading || uploading}
                                    />

                                    {!uploading ? (
                                        <div onClick={() => !uploading && openimageBrowse(imageInputRef)} style={{ cursor: 'pointer' }}>
                                            <div style={{
                                                width: '80px',
                                                height: '80px',
                                                borderRadius: '50%',
                                                background: 'linear-gradient(rgb(13, 21, 43) 0%, rgb(44, 71, 145) 100%)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                margin: '0 auto 20px',
                                                boxShadow: '0 10px 30px rgba(102, 126, 234, 0.4)'
                                            }}>
                                                <FaVideo size={32} color="white" />
                                            </div>
                                            <h3 style={{ color: '#2d3748', margin: '0 0 8px', fontSize: '18px', fontWeight: '600' }}>
                                                Upload Video
                                            </h3>
                                            <h2 style={{ color: "#2d3748", margin: '0 0 20px', fontSize: '16px' }}>
                                                Click to browse or drag & drop
                                            </h2>
                                            <button
                                                disabled={loading || uploading}
                                                style={{
                                                    padding: '12px 32px',
                                                    borderRadius: '10px',
                                                    border: 'none',
                                                    background: 'linear-gradient(rgb(13, 21, 43) 0%, rgb(44, 71, 145) 100%)',
                                                    color: 'white',
                                                    fontWeight: '600',
                                                    cursor: loading || uploading ? 'not-allowed' : 'pointer',
                                                    fontSize: '14px',
                                                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                                                    transition: 'all 0.3s ease'
                                                }}
                                            >
                                                Choose File
                                            </button>
                                        </div>
                                    ) : (
                                        <div style={{ padding: '20px' }}>
                                            <div style={{
                                                width: '80px',
                                                height: '80px',
                                                borderRadius: '50%',
                                                background: 'linear-gradient(rgb(13, 21, 43) 0%, rgb(44 54 82) 100%), url(/static/media/background.e90c60b….png)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                margin: '0 auto 20px',
                                                animation: 'pulse 2s ease-in-out infinite'
                                            }}>
                                                <FaUpload size={32} color="white" />
                                            </div>
                                            <h3 style={{ color: '#2d3748', margin: '0 0 8px', fontSize: '18px', fontWeight: '600' }}>
                                                Uploading...
                                            </h3>
                                            <h3 style={{ color: '#2d3748', margin: '0 0 20px', fontSize: '24px', fontWeight: '700' }}>
                                                {uploadProgress}%
                                            </h3>
                                            <div style={{
                                                width: '100%',
                                                height: '8px',
                                                backgroundColor: '#e2e8f0',
                                                borderRadius: '10px',
                                                overflow: 'hidden',
                                                marginBottom: '12px'
                                            }}>
                                                <div style={{
                                                    width: `${uploadProgress}%`,
                                                    height: '100%',
                                                    background: 'linear-gradient(90deg, #48bb78 0%, #38a169 100%)',
                                                    transition: 'width 0.3s ease',
                                                    borderRadius: '10px'
                                                }} />
                                            </div>
                                            <h3 style={{ color: '#2d3748', fontSize: '12px', margin: 0 }}>
                                                Please don't close this window
                                            </h3>
                                        </div>
                                    )}

                                    {builderVideos.video && (
                                        <div style={{ marginTop: '24px', position: 'relative' }}>
                                            <video
                                                style={{
                                                    width: '100%',
                                                    maxHeight: '200px',
                                                    borderRadius: '12px',
                                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                                                }}
                                                src={builderVideos.video}
                                                controls
                                            />
                                            <button
                                                onClick={(e) => handleRemoveImage(e)}
                                                disabled={loading || uploading}
                                                style={{
                                                    position: 'absolute',
                                                    top: '8px',
                                                    right: '14px',
                                                    width: '4px',
                                                    height: '23px',
                                                    borderRadius: '25%',
                                                    border: 'none',
                                                    background: 'linear-gradient(135deg, rgb(245, 247, 250) 0%, rgb(195, 207, 226) 100%)',
                                                    color: 'rgb(45, 55, 72)',
                                                    fontSize: 'large',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    transition: 'all 0.3s ease'
                                                }}
                                            >
                                                x
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Thumbnail Upload */}
                                <div style={{
                                    border: '3px dashed #e0e0e0',
                                    borderRadius: '16px',
                                    padding: '32px',
                                    textAlign: 'center',
                                    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                                    position: 'relative',
                                    transition: 'all 0.3s ease'
                                }}>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        ref={thumbnailInputRef}
                                        onChange={handleImageInput}
                                        hidden
                                        disabled={loading || uploading}
                                    />

                                    <div onClick={() => openimageBrowse(thumbnailInputRef)} style={{ cursor: 'pointer' }}>
                                        <div style={{
                                            width: '80px',
                                            height: '80px',
                                            borderRadius: '50%',
                                            background: 'linear-gradient(rgb(13, 21, 43) 0%, rgb(44, 71, 145) 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: '0 auto 20px',
                                            boxShadow: 'rgba(102, 126, 234, 0.4) 0px 10px 30px'
                                        }}>
                                            <FaImage size={32} color="white" />
                                        </div>
                                        <h3 style={{ color: '#2d3748', margin: '0 0 8px', fontSize: '18px', fontWeight: '600' }}>
                                            Upload Thumbnail
                                        </h3>
                                        <h3 style={{ color: "#2d3748", margin: '0 0 20px', fontSize: '16px' }}>
                                            Click to browse or drag & drop
                                        </h3>
                                        <button
                                            disabled={loading || uploading}
                                            style={{
                                                padding: '12px 32px',
                                                borderRadius: '10px',
                                                border: 'none',
                                                background: 'linear-gradient(rgb(13, 21, 43) 0%, rgb(44, 71, 145) 100%)',
                                                color: 'white',
                                                fontWeight: '600',
                                                cursor: loading || uploading ? 'not-allowed' : 'pointer',
                                                fontSize: '14px',
                                                boxShadow: 'rgba(102, 126, 234, 0.4) 0px 10px 30px',
                                                transition: 'all 0.3s ease'
                                            }}
                                        >
                                            Choose File
                                        </button>
                                    </div>

                                    {builderVideos.thumbnail && (
                                        <div style={{ marginTop: '24px', position: 'relative' }}>
                                            <img
                                                style={{
                                                    width: '100%',
                                                    maxHeight: '200px',
                                                    borderRadius: '12px',
                                                    objectFit: 'contain',
                                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                                                }}
                                                src={builderVideos.thumbnail}
                                                alt="thumbnail"
                                            />
                                            <button
                                                onClick={(e) => handleThumbnailRemove(e)}
                                                disabled={loading || uploading}
                                                style={{
                                                    position: 'absolute',
                                                    top: '8px',
                                                    right: '14px',
                                                    width: '4px',
                                                    height: '23px',
                                                    borderRadius: '25%',
                                                    border: 'none',
                                                    background: 'linear-gradient(135deg, rgb(245, 247, 250) 0%, rgb(195, 207, 226) 100%)',
                                                    color: 'rgb(45, 55, 72)',
                                                    fontSize: 'large',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    transition: 'all 0.3s ease'
                                                }}
                                            >
                                                x
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                <button
                                className="cancel-btn"
                                    onClick={(e) => handleBack(e)}
                                    disabled={loading || uploading}
                                    style={{
                                        padding: '14px 32px',
                                        borderRadius: '12px',
                                        // border: '2px solid #e2e8f0',
                                        background: 'white',
                                        // color: '#2d3748',
                                        fontWeight: '600',
                                        cursor: loading || uploading ? 'not-allowed' : 'pointer',
                                        opacity: loading || uploading ? 0.5 : 1,
                                        fontSize: '14px',
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={(e) => handleSave(e)}
                                    disabled={
                                        loading ||
                                        uploading ||
                                        !builderVideos.video ||
                                        !builderVideos.thumbnail
                                    }
                                    style={{
                                        padding: '14px 32px',
                                        borderRadius: '12px',
                                        border: 'none',
                                        background:
                                            'linear-gradient(rgb(13, 21, 43) 0%, rgb(44, 71, 145) 100%)',
                                        color: 'white',
                                        fontWeight: '600',
                                        cursor:
                                            loading || uploading || !builderVideos.video || !builderVideos.thumbnail
                                                ? 'not-allowed'
                                                : 'pointer',
                                        opacity:
                                            loading || uploading || !builderVideos.video || !builderVideos.thumbnail
                                                ? 0.5
                                                : 1,
                                        fontSize: '14px',
                                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                                        transition: 'all 0.3s ease',
                                    }}
                                >
                                    {loading ? "Saving..." : "Save Video"}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '24px' }}>
                            {videos?.length ? (
                                videos.map((video, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            position: 'relative',
                                            borderRadius: '16px',
                                            overflow: 'hidden',
                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                            transition: 'all 0.3s ease',
                                            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                                        }}
                                    >
                                        {playingIndex === index ? (
                                            <video
                                                src={video.video}
                                                style={{
                                                    width: '100%',
                                                    height: '200px',
                                                    objectFit: 'cover',
                                                    display: 'block',
                                                }}
                                                controls
                                                autoPlay
                                                onEnded={() => setPlayingIndex(null)}
                                            />
                                        ) : (
                                            <>
                                                <img
                                                    src={video.thumbnail}
                                                    alt="video thumbnail"
                                                    style={{
                                                        width: '100%',
                                                        height: '200px',
                                                        objectFit: 'cover',
                                                        display: 'block',
                                                    }}
                                                />
                                                <button
                                                    onClick={() => setPlayingIndex(index)}
                                                    style={{
                                                        position: 'absolute',
                                                        top: '50%',
                                                        left: '50%',
                                                        transform: 'translate(-50%, -50%)',
                                                        backgroundColor: 'rgba(0,0,0,0.6)',
                                                        border: 'none',
                                                        borderRadius: '50%',
                                                        width: '60px',
                                                        height: '60px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        cursor: 'pointer',
                                                        color: 'white',
                                                        fontSize: '24px',
                                                    }}
                                                    aria-label="Play Video"
                                                >
                                                    ▶
                                                </button>
                                            </>
                                        )}

                                        <div style={{
                                            position: 'absolute',
                                            top: '12px',
                                            right: '12px'
                                        }}>
                                            <button
                                                onClick={() => {
                                                    if (!loading && !uploading) {
                                                        dispatch(removeProjectVideo({
                                                            project_id: project?.data?._id,
                                                            video_id: video.id
                                                        })).then(() => {
                                                            dispatch(viewProjectVideos(project.data._id));
                                                        }).catch((err) => {
                                                            toast.error("Failed to remove video");
                                                            console.error(err);
                                                        });
                                                    }
                                                }}
                                                disabled={loading || uploading}
                                                style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    borderRadius: '12px',
                                                    border: 'none',
                                                    background: 'transparent',
                                                    color: 'red',
                                                    cursor: loading || uploading ? 'not-allowed' : 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                                    transition: 'all 0.3s ease'
                                                }}
                                            >
                                                <h3 style={{ color: "rgb(54, 58, 75)", marginBottom: "13px" }}>x</h3>
                                            </button>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div style={{ textAlign: 'center', color: '#718096', fontSize: '18px', marginTop: '48px' }}>
                                    No videos found for this project.
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                @keyframes pulse {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                }
            `}</style>
        </div>,
        document.body
    );
};

export default VideoModal;
